import { useContext } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { Button, ButtonDropdown, DropdownItem, DropdownMenu, DropdownToggle } from "reactstrap";

import * as actions from "../actions/ui-actions";
import { updateWorldMetadata } from "../actions/world-actions";
import { MODALS, TOOLS } from "../constants/constants";
import StageSwitcher from "./stage-switcher";
import { TapToEditLabel } from "./tap-to-edit-label";
import UndoRedoControls from "./undo-redo-controls";

import { createWorld } from "../../actions/main-actions";
import { EditorContext } from "../../components/editor-context";
import { useDismissibleMenu } from "../../hooks/useDismissibleMenu";
import { useEditorSelector } from "../../hooks/redux";
import { FULLSCREEN_SHORTCUT_LABEL } from "../../hooks/useFullscreen";
import { forgivingPress } from "../utils/pointer";
import { ToolButton } from "./tool-button";

const Toolbar = () => {
  const dispatch = useDispatch();
  const selectedToolId = useEditorSelector((state) => state.ui.selectedToolId);
  const metadata = useEditorSelector((state) => state.world.metadata);
  const isInWalkthrough = useEditorSelector((state) => !!state.ui.tutorial.stepSet);

  const {
    usingLocalStorage,
    saveWorldAnd,
    saveWorld,
    save,
    saveAndExit,
    exitWithoutSaving,
    hasUnsavedChanges,
    isFullscreen,
    canFullscreen,
    toggleFullscreen,
  } = useContext(EditorContext);
  const { menuProps, open, toggle } = useDismissibleMenu("author");

  const renderTool = (toolId: TOOLS) => (
    <ToolButton
      key={toolId}
      toolId={toolId}
      selected={selectedToolId === toolId}
      onSelect={(id) => dispatch(actions.selectToolId(id))}
    />
  );

  const onNameChange = (name: string) => {
    dispatch(updateWorldMetadata("root", { ...metadata, name }));
  };

  const onUnpublish = () => {
    dispatch(updateWorldMetadata("root", { ...metadata, published: false }));
    saveWorld();
  };

  const renderLeft = () => {
    if (usingLocalStorage) {
      // The game lives in this browser: there's no menu, just a way out (to My
      // Games, which lists it) and a way to keep it safe. Signed-out kids are
      // nudged to make an account; signed-in kids (who opened it from "On this
      // computer" in My Games) can move it straight into theirs.
      const signedIn = !!window.store.getState().me;
      const sendToAccount = `/join-send-world?storageKey=${metadata.id}`;
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Button onClick={() => saveAndExit("/dashboard")} title="Save, then go to My Games">
            <i className="fa fa-sign-out fa-flip-horizontal" style={{ marginRight: 6 }} />
            Save &amp; Exit
          </Button>
          <div className="create-account-notice">
            <span>Only saved on this computer</span>
            {signedIn ? (
              <Button color="success" onClick={() => saveAndExit(sendToAccount)}>
                Save to My Account
              </Button>
            ) : (
              <Link
                to={{
                  pathname: `/join`,
                  search: new URLSearchParams({
                    why: ` to save "${metadata.name || "your game"}"`,
                    redirectTo: sendToAccount,
                  }).toString(),
                }}
              >
                <Button color="success">Create Account</Button>
              </Link>
            )}
          </div>
        </div>
      );
    }

    return (
      <div style={{ display: "flex", alignItems: "center" }}>
        <ButtonDropdown {...menuProps} data-tutorial-id="main-menu" isOpen={open} toggle={toggle}>
          <DropdownToggle {...forgivingPress()}>
            <i className="fa fa-ellipsis-v" />
          </DropdownToggle>
          <DropdownMenu>
            <DropdownItem disabled={!hasUnsavedChanges} onClick={() => save()}>
              <i className="fa fa-save fa-fw" style={{ marginRight: 8 }} />
              {/* A game has one saved copy, and that's what players get - so
                  once it's published, saving is how you update it. */}
              {metadata.published ? "Save & Update Published Game" : "Save"}
            </DropdownItem>
            <DropdownItem onClick={() => saveAndExit("/dashboard")}>
              <i className="fa fa-sign-out fa-fw fa-flip-horizontal" style={{ marginRight: 8 }} />
              Save &amp; Exit
            </DropdownItem>
            <DropdownItem
              onClick={() => {
                if (window.confirm("Exit without saving? Your unsaved changes will be lost.")) {
                  exitWithoutSaving("/dashboard");
                }
              }}
            >
              <i className="fa fa-fw" style={{ marginRight: 8 }} />
              Exit Without Saving
            </DropdownItem>
            <DropdownItem divider />
            <DropdownItem
              onClick={() => {
                save().then(() => dispatch(createWorld({ from: metadata.id })));
              }}
            >
              <i className="fa fa-fw" style={{ marginRight: 8 }} />
              Duplicate This World
            </DropdownItem>
            {canFullscreen && (
              <>
                <DropdownItem divider />
                <DropdownItem
                  onClick={toggleFullscreen}
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <i
                    className={`fa ${isFullscreen ? "fa-compress" : "fa-expand"} fa-fw`}
                    style={{ marginRight: 8 }}
                  />
                  {isFullscreen ? "Exit Full Screen" : "Show Full Screen"}
                  <span style={{ marginLeft: "auto", paddingLeft: 24, opacity: 0.5 }}>
                    {FULLSCREEN_SHORTCUT_LABEL}
                  </span>
                </DropdownItem>
              </>
            )}
            <DropdownItem divider />
            <DropdownItem onClick={() => saveWorldAnd(`/play/${metadata.id}`)}>
              <i className="fa fa-play fa-fw" style={{ marginRight: 8 }} />
              Switch to Player View...
            </DropdownItem>
            <DropdownItem divider />
            <DropdownItem onClick={() => dispatch(actions.showModal(MODALS.VIDEOS))}>
              <i className="fa fa-fw" style={{ marginRight: 8 }} />
              Tips &amp; Tricks Videos...
            </DropdownItem>
            {!isInWalkthrough && (
              <DropdownItem
                onClick={() => {
                  alert("Your current game will be saved - you can open it later from 'My Games'.");
                  saveWorldAnd("/learn");
                }}
              >
                <i className="fa fa-fw" style={{ marginRight: 8 }} />
                Learn Codako...
              </DropdownItem>
            )}
            <DropdownItem divider />
            {metadata.published ? (
              <DropdownItem onClick={onUnpublish}>
                <i className="fa fa-eye-slash fa-fw" style={{ marginRight: 8 }} />
                Unpublish Game
              </DropdownItem>
            ) : (
              <DropdownItem onClick={() => dispatch(actions.showModal(MODALS.PUBLISH))}>
                <i className="fa fa-globe fa-fw" style={{ marginRight: 8 }} />
                Publish Game...
              </DropdownItem>
            )}
          </DropdownMenu>
        </ButtonDropdown>
        <TapToEditLabel className="world-name" value={metadata.name} onChange={onNameChange} />
        {hasUnsavedChanges && (
          <i
            className="fa fa-circle"
            style={{ fontSize: "16px", color: "#ff9800", marginLeft: "8px" }}
            title="Unsaved changes"
          />
        )}
      </div>
    );
  };

  return (
    <div className="toolbar">
      <div style={{ flex: 1, textAlign: "left" }}>{renderLeft()}</div>

      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div className="button-group">{[TOOLS.POINTER].map(renderTool)}</div>
        <div className="button-group">
          {[TOOLS.CREATE_CHARACTER, TOOLS.PAINT, TOOLS.RECORD].map(renderTool)}
        </div>
        <div className="button-group">{[TOOLS.STAMP, TOOLS.TRASH].map(renderTool)}</div>
      </div>

      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 12,
        }}
      >
        <UndoRedoControls />
        <StageSwitcher />
      </div>
    </div>
  );
};

export default Toolbar;
